import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import { InputQuantity } from '@/design_system/InputQuantity/InputQuantity';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table, TextAreaCell } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import IconUndo from '@/icons/Undo.svg';
import {
  TEMPORARY_ID_PREFIX,
  useDeleteArticleActionSparePart,
  useRestoreSparePartToArticleAction,
  useUpdateArticleActionSparePart,
} from '@/models/articleActionSparePart';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';

import { SparePartSearchSelect } from '../SparePartSearchSelect';
import { SparePartTypeImage } from '../SparePartTypeImage';

import { ActionSparePartPrice } from './ActionSparePartPrice';

import './ActionSpareParts.css';

type SparePart = ArticleActionWithRelations['spareParts'][number];

const { element } = createBEMClasses('action-spare-parts');

type SparePartComponentProps = {
  actionId: string;
  sparePart?: SparePart;
  previousSparePart?: SparePart;
};

type NotDeletedSparePartComponentProps = {
  actionId: string;
  sparePart: SparePart;
  previousSparePart?: SparePart;
};

export const ActionSpareParts = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view, article } = useArticleContext();

  const showReferenceNumber = view.services.actions.fields.spareParts.referenceNumber.shown;
  const canAddRemoveSpareParts = view.services.actions.fields.spareParts.canAddRemoveSparePart;
  const canUpdateQuantity =
    view.services.actions.fields.spareParts.quantity.editable && !!action?.spareParts.length;

  const columnWidths = [
    'minmax(350px, 1fr)',
    showReferenceNumber && 'minmax(100px, 1fr)',
    'minmax(auto, 128px)',
    canAddRemoveSpareParts && 'minmax(auto, 56px)',
  ];

  const currentSpareParts = action ? action.spareParts : [];

  const deletedSpareParts = previousAction
    ? previousAction.spareParts.filter(
        (previousSparePart) =>
          !currentSpareParts.some((sparePart) => sparePart.stableId === previousSparePart.stableId)
      )
    : [];

  const spareParts = [...deletedSpareParts, ...currentSpareParts];

  return (
    <Stack gap="0.5rem">
      {!!spareParts.length && (
        <Stack gap="0.25rem">
          <span className="label-100-regular">
            <Trans id="article.form.actions.spare-parts.label">Spare parts</Trans>
          </span>

          <Table bordered columnWidths={columnWidths} variant="grid">
            <Header>
              <Row>
                <Column>
                  <span className={!action ? 'text-disabled' : ''}>
                    <Trans id="spare-parts-table.column.header.reference">Reference</Trans>
                  </span>
                </Column>
                {showReferenceNumber && (
                  <Column>
                    <span className={!action ? 'text-disabled' : ''}>
                      <Trans id="spare-parts-table.column.header.reference-number">
                        Reference number
                      </Trans>
                    </span>
                  </Column>
                )}
                <Column justifyContent="center">
                  <span className={!action ? 'text-disabled' : ''}>
                    <Trans id="spare-parts-table.column.header.quantity">Quantity</Trans>
                  </span>
                </Column>
                {canAddRemoveSpareParts && action && <Column></Column>}
              </Row>
            </Header>
            <Body>
              {spareParts.map((sparePart) => (
                <ActionSparePartRow
                  key={sparePart.id}
                  sparePart={sparePart}
                  action={action}
                  previousAction={previousAction}
                />
              ))}
            </Body>
          </Table>
        </Stack>
      )}
      {(canAddRemoveSpareParts || canUpdateQuantity) && article.step?.step === 'repair' && (
        <AlertBar
          title={
            <Trans id="spare-parts-table.repair.warning">
              New spare parts added during the repair step are not charged
            </Trans>
          }
        />
      )}
      {canAddRemoveSpareParts && action && <AddNewSparePart actionId={action.id} />}
    </Stack>
  );
};

const ActionSparePartRow = ({
  sparePart,
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  sparePart: SparePart;
}) => {
  const { view, article } = useArticleContext();

  const previousSparePart = previousAction?.spareParts.find(
    (sparePartSnapshot) => sparePartSnapshot.stableId == sparePart.stableId
  );

  const currentSparePart = action?.spareParts.find(
    (currentSparePart) => currentSparePart.stableId === sparePart.stableId
  );
  const isCreationStep = article.step?.step === 'creation';

  const showReferenceNumber = view.services.actions.fields.spareParts.referenceNumber.shown;
  const canAddRemoveSpareParts = view.services.actions.fields.spareParts.canAddRemoveSparePart;

  const actionId = action ? action.id : previousAction!.id;

  const currentActionAlreadyHasSparePartType = action?.spareParts.some(
    (sparePartInCurrentSnapshot) =>
      sparePartInCurrentSnapshot.sparePartTypeId === sparePart.sparePartTypeId
  );

  return (
    (currentSparePart || previousSparePart) && (
      <Row
        className={element('row', {
          new: !!previousAction && !previousSparePart && !isCreationStep,
          deleted: !currentSparePart,
        })}
      >
        <Cell>
          <ActionSparePartReferenceCell
            actionId={actionId}
            sparePart={currentSparePart}
            previousSparePart={previousSparePart}
          />
        </Cell>
        {showReferenceNumber && (
          <ActionSparePartReferenceNumber
            actionId={actionId}
            sparePart={currentSparePart}
            previousSparePart={previousSparePart}
          />
        )}
        <ActionSparePartQuantityCell
          actionId={actionId}
          sparePart={currentSparePart}
          previousSparePart={previousSparePart}
        />
        {canAddRemoveSpareParts && !!action && (
          <Cell>
            {!currentSparePart && !currentActionAlreadyHasSparePartType ? (
              <ActionSparePartRestoreButton actionId={actionId} sparePart={sparePart} />
            ) : (
              <ActionSparePartDeleteButton actionId={actionId} sparePart={sparePart} />
            )}
          </Cell>
        )}
      </Row>
    )
  );
};

const ActionSparePartReferenceCell = ({
  actionId,
  sparePart,
  previousSparePart,
}: SparePartComponentProps) => {
  const { _db } = useDatabaseTranslation();
  const { view } = useArticleContext();
  const { view: requestView } = useRequestContext();

  const showWorkshopPrice = requestView.workshopPrice.shown;
  const showOrganizationPrice = requestView.organizationPrice.shown;

  const sparePartType: SparePart['sparePartType'] = sparePart
    ? sparePart.sparePartType
    : previousSparePart!.sparePartType;

  const sparePartName = _db(sparePartType.name);

  return (
    <Stack row alignItems="center" gap="8px" flexWrap="nowrap">
      <SparePartTypeImage sparePartType={sparePartType} />
      <Stack gap="4px">
        <InlineText color={!sparePart ? 'disabled' : 'primary'}>{sparePartName}</InlineText>
        {view.services.actions.fields.spareParts.prices.shown && (
          <>
            {showWorkshopPrice && (
              <ActionSparePartPrice
                sparePart={sparePart || previousSparePart!}
                priceType="workshop"
                disabled={!sparePart}
                editable={view.services.actions.fields.spareParts.workshopPrice.editable}
                actionId={actionId}
                previousSparePart={previousSparePart}
              />
            )}
            {showOrganizationPrice && (
              <ActionSparePartPrice
                sparePart={sparePart || previousSparePart!}
                priceType="organization"
                disabled={!sparePart}
                editable={view.services.actions.fields.spareParts.organizationPrice.editable}
                actionId={actionId}
                previousSparePart={previousSparePart}
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

const ActionSparePartReferenceNumber = ({
  actionId,
  sparePart,
  previousSparePart,
}: SparePartComponentProps) => {
  const { view } = useArticleContext();
  const referenceNumber = sparePart
    ? sparePart.referenceNumber
    : previousSparePart!.referenceNumber;

  return view.services.actions.fields.spareParts.referenceNumber.editable && sparePart ? (
    <ActionSparePartEditableReferenceNumber
      actionId={actionId}
      sparePart={sparePart}
      previousSparePart={previousSparePart}
    />
  ) : (
    <Cell>
      <InlineText color={!sparePart ? 'disabled' : 'primary'}>{referenceNumber || '-'}</InlineText>
    </Cell>
  );
};

const ActionSparePartEditableReferenceNumber = ({
  actionId,
  sparePart,
  previousSparePart,
}: NotDeletedSparePartComponentProps) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const [referenceNumber, setReferenceNumber] = useState(sparePart.referenceNumber || null);
  const { mutateAsync: updateAction } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId,
    sparePartId: sparePart.id,
  });

  const hasBeenEdited =
    previousSparePart && previousSparePart.referenceNumber !== sparePart.referenceNumber;

  return (
    <TextAreaCell
      ariaLabel={t({
        id: 'spare-parts-table.column.reference-number.label',
        message: 'Reference number of the spare part',
      })}
      placeholder={t({
        id: 'spare-parts-table.column.reference-number.placeholder',
        message: 'Add a reference number...',
      })}
      rows={1}
      disabled={sparePart.id.includes(TEMPORARY_ID_PREFIX)}
      cellProps={{
        className: hasBeenEdited ? element('row-reference-number', { edited: true }) : '',
      }}
      value={referenceNumber || undefined}
      onChange={(event) =>
        setReferenceNumber(event.target.value === '' ? null : event.target.value)
      }
      onBlur={() => {
        const currentReferenceNumber = sparePart.referenceNumber;

        updateAction({
          referenceNumber,
        }).catch(() => setReferenceNumber(currentReferenceNumber));
      }}
    >
      {referenceNumber ? (
        referenceNumber
      ) : (
        <span className="text-disabled">
          <Trans id="spare-parts-table.column.reference-number.placeholder">
            Add a reference number...
          </Trans>
        </span>
      )}
    </TextAreaCell>
  );
};

const ActionSparePartQuantityCell = ({
  actionId,
  sparePart,
  previousSparePart,
}: SparePartComponentProps) => {
  const { _db } = useDatabaseTranslation();
  const { view } = useArticleContext();

  const isEditable = sparePart && view.services.actions.fields.spareParts.quantity.editable;
  const hasBeenEdited =
    previousSparePart && sparePart && previousSparePart.quantity !== sparePart.quantity;
  const quantity = sparePart ? sparePart.quantity : previousSparePart!.quantity;
  const unit = sparePart ? sparePart.sparePartType.unit : previousSparePart!.sparePartType.unit;

  return (
    <Cell justifyContent="center" className={element('row-quantity', { edited: hasBeenEdited })}>
      <Stack alignItems="center">
        {isEditable ? (
          <ActionSparePartEditableQuantity actionId={actionId} sparePart={sparePart} />
        ) : (
          <InlineText className={!sparePart ? 'text-disabled' : ''}>{quantity}</InlineText>
        )}
        {unit && (
          <InlineText size="200" color="secondary">
            <Trans id="spare-parts-table.column.quantity.unit">unit:</Trans> {_db(unit)}
          </InlineText>
        )}
      </Stack>
    </Cell>
  );
};

const ActionSparePartEditableQuantity = ({
  actionId,
  sparePart,
}: NotDeletedSparePartComponentProps) => {
  const { t } = useLingui();
  const { article, request, errors } = useArticleContext();
  const [value, setValue] = useState(sparePart.quantity);
  const { mutateAsync: updateSparePart } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId: actionId,
    sparePartId: sparePart.id,
  });

  const hasError = !!errors.services?.actions?.actions
    ?.find((act) => act.id === actionId)
    ?.spareParts?.find((sparePartError) => sparePartError.id === sparePart.id)?.quantityIsNull;

  const debouncedUpdateSparePart = useMemo(() => {
    return debounce(updateSparePart, 500);
  }, [updateSparePart]);

  return (
    <InputQuantity
      ariaLabel={t({
        id: 'spare-parts-table.column.quantity.label',
        message: 'Quantity of spare parts',
      })}
      value={value}
      onChange={(newQuantity) => {
        setValue(newQuantity);
        debouncedUpdateSparePart({ quantity: newQuantity });
      }}
      disabled={sparePart.id.includes(TEMPORARY_ID_PREFIX)}
      minValue={1}
      isInvalid={hasError}
    />
  );
};

export const ActionSparePartDeleteButton = ({
  actionId,
  sparePart,
}: {
  actionId: string;
  sparePart: SparePart;
}) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const { mutate: deleteSparePart } = useDeleteArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId,
  });

  return (
    <Button
      ariaLabel={t({
        id: 'spare-parts-table.column.actions.delete',
        message: 'Delete spare part',
      })}
      variant="secondary-danger"
      disabled={sparePart.id.includes(TEMPORARY_ID_PREFIX)}
      iconOnly
      size="medium"
      onPress={() => deleteSparePart(sparePart.id)}
    >
      <IconTrash />
    </Button>
  );
};

export const ActionSparePartRestoreButton = ({
  actionId,
  sparePart,
}: {
  actionId: string;
  sparePart: SparePart;
}) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const { mutate: restoreSparePart, isPending: isRestoreSparePartPending } =
    useRestoreSparePartToArticleAction({
      articleId: article.id,
      requestId: request.id,
      actionId,
    });

  return (
    <Button
      ariaLabel={t({
        id: 'spare-parts-table.column.actions.restore',
        message: 'Restore spare part',
      })}
      variant="secondary"
      iconOnly
      size="medium"
      onPress={() => {
        restoreSparePart({ sparePart });
      }}
      isLoading={isRestoreSparePartPending}
    >
      <IconUndo />
    </Button>
  );
};

const AddNewSparePart = ({ actionId }: { actionId: string }) => {
  const [displaySparePartSelection, setDisplaySparePartSelection] = useState(false);

  if (!displaySparePartSelection) {
    return <AddNewSparePartButton onPress={() => setDisplaySparePartSelection(true)} />;
  }

  return (
    <SparePartSearchSelect
      actionId={actionId}
      onBlur={() => {
        setDisplaySparePartSelection(false);
      }}
    />
  );
};

const AddNewSparePartButton = ({ onPress }: { onPress: () => void }) => (
  <Button style={{ alignSelf: 'flex-start' }} variant="link-subtle" onPress={onPress}>
    <Stack row alignItems="center">
      <IconAdd />
      <InlineText size="200" weight="medium">
        <Trans id="spare-parts-table.add_spare_part.button.label">Add a spare part</Trans>
      </InlineText>
    </Stack>
  </Button>
);
